import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { selectSidebarStyle } from "state/application/applicationSlice";

import Icon from "./Icon";
import BrandLogo from "./BrandLogo";
import "./shell.css";

function collectCandidates(nav) {
  return nav.flatMap((n) => {
    if (n.type === "item" && n.route) return [{ key: n.key, route: n.route }];
    if (n.type === "group") {
      return n.children.filter((c) => c.route).map((c) => ({ key: c.key, route: c.route }));
    }
    return [];
  });
}

function matchActiveKey(nav, pathname) {
  const candidates = collectCandidates(nav);
  const matched = candidates.filter(
    (c) => pathname === c.route || pathname.startsWith(`${c.route}/`)
  );
  const best = matched.reduce(
    (acc, cur) => (!acc || cur.route.length > acc.route.length ? cur : acc),
    null
  );
  return best ? best.key : null;
}

function activateOnKey(handler) {
  return (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handler(e);
    }
  };
}

function FlyoutMenu({ group, activeKey, top, onSelect, onClose }) {
  return (
    <div
      className="rec-flyout"
      style={{ top: Math.min(top, window.innerHeight - 260) }}
      onMouseLeave={onClose}
    >
      <div className="rec-flyout-title">{group.name}</div>
      {group.children.map((c) => {
        const click = () => onSelect(c.route);
        return (
          <div
            key={c.key}
            role="button"
            tabIndex={0}
            className={`rec-nav-item${activeKey === c.key ? " active" : ""}`}
            style={{ height: 42 }}
            onClick={click}
            onKeyDown={activateOnKey(click)}
          >
            <span className="rec-nav-label" style={{ fontSize: 13.5 }}>
              {c.name}
            </span>
          </div>
        );
      })}
    </div>
  );
}

FlyoutMenu.propTypes = {
  group: PropTypes.shape({
    name: PropTypes.string,
    children: PropTypes.arrayOf(
      PropTypes.shape({ key: PropTypes.string, name: PropTypes.string, route: PropTypes.string })
    ),
  }).isRequired,
  activeKey: PropTypes.string,
  top: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

FlyoutMenu.defaultProps = { activeKey: null };

export default function Sidebar({ nav, mini, show, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarStyle = useSelector(selectSidebarStyle);

  const isRail = sidebarStyle === "rail";
  const isMini = isRail || mini;

  const activeKey = useMemo(() => matchActiveKey(nav, location.pathname), [nav, location.pathname]);

  const activeGroup = useMemo(
    () => nav.find((n) => n.type === "group" && n.children.some((c) => c.key === activeKey))?.key,
    [nav, activeKey]
  );

  const [open, setOpen] = useState(activeGroup ? [activeGroup] : []);
  useEffect(() => {
    if (activeGroup) {
      setOpen((o) => (o.includes(activeGroup) ? o : [...o, activeGroup]));
    }
  }, [activeGroup]);

  const [flyout, setFlyout] = useState(null);
  const [tip, setTip] = useState(null);

  const toggle = (k) => setOpen((o) => (o.includes(k) ? o.filter((x) => x !== k) : [...o, k]));

  const go = (route) => {
    if (route) navigate(route);
    if (onClose) onClose();
  };

  const showTip = (e, name) => {
    if (isMini) {
      const r = e.currentTarget.getBoundingClientRect();
      setTip({ name, top: r.top + 8 });
    }
  };
  const hideTip = () => setTip(null);

  const onGroupClick = (n, e) => {
    if (isMini) {
      const r = e.currentTarget.getBoundingClientRect();
      setFlyout((cur) => (cur && cur.key === n.key ? null : { key: n.key, top: r.top }));
    } else {
      toggle(n.key);
    }
  };

  const renderItem = (n) => {
    const click = () => go(n.route);
    return (
      <div
        key={n.key}
        role="button"
        tabIndex={0}
        className={`rec-nav-item${activeKey === n.key ? " active" : ""}`}
        onClick={click}
        onKeyDown={activateOnKey(click)}
        onMouseEnter={(e) => showTip(e, n.name)}
        onMouseLeave={hideTip}
      >
        <Icon name={n.icon || "circle"} />
        {!isMini && <span className="rec-nav-label">{n.name}</span>}
      </div>
    );
  };

  const renderGroup = (n) => {
    const isOpen = open.includes(n.key);
    const hasActive = n.children.some((c) => c.key === activeKey);
    const click = (e) => onGroupClick(n, e);
    return (
      <div key={n.key}>
        <div
          role="button"
          tabIndex={0}
          className={`rec-nav-item${hasActive && isMini ? " active" : ""}`}
          onClick={click}
          onKeyDown={activateOnKey(click)}
          onMouseEnter={(e) => showTip(e, n.name)}
          onMouseLeave={hideTip}
        >
          <Icon name={n.icon || "folder"} />
          {!isMini && <span className="rec-nav-label">{n.name}</span>}
          {!isMini && <Icon name="expand_more" className={`chev${isOpen ? " open" : ""}`} />}
        </div>
        {!isMini && (
          <div
            className="rec-nav-sub"
            style={{ maxHeight: isOpen ? `${n.children.length * 48}px` : "0" }}
          >
            {n.children.map((c) => {
              const clickChild = () => go(c.route);
              return (
                <div
                  key={c.key}
                  role="button"
                  tabIndex={0}
                  className={`rec-nav-subitem${activeKey === c.key ? " active" : ""}`}
                  onClick={clickChild}
                  onKeyDown={activateOnKey(clickChild)}
                >
                  <span className="rec-nav-label">{c.name}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderEntry = (n, idx) => {
    if (n.type === "section") {
      return (
        <div key={n.key || `section-${idx}`} className="rec-nav-section">
          {isMini ? n.name.slice(0, 3) : n.name}
        </div>
      );
    }
    if (n.type === "item") return renderItem(n);
    if (n.type === "group") return renderGroup(n);
    return null;
  };

  const flyoutGroup = flyout ? nav.find((n) => n.key === flyout.key) : null;

  return (
    <>
      <aside className={`rec-sidebar${isMini ? " mini" : ""}${show ? " show" : ""}`}>
        <div
          className="rec-sidebar-head"
          style={isMini ? { justifyContent: "center", padding: 0 } : undefined}
        >
          {/* no forceWhite: BrandLogo already picks the mono-white logo on
              dark theme or branded sidebar */}
          {isMini ? <BrandLogo height={38} /> : <BrandLogo height={84} />}
        </div>

        <nav className="rec-sidebar-scroll">{nav.map(renderEntry)}</nav>
      </aside>

      {tip && isMini && (
        <div className="rec-mini-tip" style={{ left: 84, top: tip.top }}>
          {tip.name}
        </div>
      )}

      {flyoutGroup && (
        <FlyoutMenu
          group={flyoutGroup}
          activeKey={activeKey}
          top={flyout.top}
          onSelect={(route) => {
            go(route);
            setFlyout(null);
          }}
          onClose={() => setFlyout(null)}
        />
      )}
    </>
  );
}

Sidebar.propTypes = {
  nav: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(["item", "section", "group"]),
      key: PropTypes.string,
      name: PropTypes.string,
      icon: PropTypes.string,
      route: PropTypes.string,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          name: PropTypes.string,
          route: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  mini: PropTypes.bool,
  show: PropTypes.bool,
  onClose: PropTypes.func,
};

Sidebar.defaultProps = {
  mini: false,
  show: false,
  onClose: undefined,
};
